import { buildAnchorId, buildAnchorIdPrefix } from '@/components/helpers'
import { search, SearchResult } from '@/components/search/helpers'
import { Button } from '@repo/ui/button'
import { CardContent } from '@repo/ui/card'
import { CommandShortcut } from '@repo/ui/command'
import { useT } from '@repo/ui/hooks'
import { Input } from '@repo/ui/input'
import { SearchIcon } from 'lucide-react'
import {
  MutableRefObject,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

export function Search<T extends Record<string, unknown>>({
  children,
  disabled = false,
  items,
}: {
  children: (props: {
    currentMatch: number
    expandedItems: boolean
    matchCount: number
    matchRefs: MutableRefObject<string[]>
    query: string
    searchResults: SearchResult<T>
  }) => ReactNode
  disabled?: boolean
  items: T[]
}) {
  const { tc } = useT()

  const [query, setQuery] = useState('')
  const [currentMatch, setCurrentMatch] = useState(0)
  const [matchCount, setMatchCount] = useState(0)
  const matchRefs = useRef<string[]>([])
  const [expandedItems, setExpandedItems] = useState(false)

  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    // Only focus if not already focused
    if (inputRef.current && document.activeElement !== inputRef.current)
      inputRef.current.focus()
  }, [disabled])

  const searchResults = useMemo(() => {
    return search(items, query, ['title', 'content'])
  }, [query, items])

  useEffect(() => {
    if (disabled) {
      setQuery('')
      setCurrentMatch(0)
      setMatchCount(0)
      matchRefs.current = []
      setExpandedItems(false)
    }
  }, [disabled])

  const handleMatchCount = useCallback(() => {
    // Set match count and current match when search results change
    if (searchResults.length > 0) {
      const total = searchResults.reduce((c, result) => {
        let count = c
        const { matches } = result
        count += (matches.title?.length || 0) + (matches.content?.length || 0)
        return count
      }, 0)
      setMatchCount(total)
    } else {
      setMatchCount(0)
    }
    setCurrentMatch(0)
  }, [searchResults])

  const handleMatchRefs = useCallback(() => {
    // Build refs for all matches
    const refs: string[] = []
    if (searchResults.length > 0) {
      for (const result of searchResults) {
        const { matches, item } = result
        const titleMatches = matches.title || []
        const contentMatches = matches.content || []
        for (let i = 0; i < titleMatches.length; i++) {
          if (!item.id) throw new Error('Item is missing id')
          if (typeof item.id !== 'string')
            throw new Error('Item id is not a string')
          refs.push(buildAnchorId(buildAnchorIdPrefix(item.id, 'title'), i))
        }
        for (let i = 0; i < contentMatches.length; i++) {
          if (!item.id) throw new Error('Item is missing id')
          if (typeof item.id !== 'string')
            throw new Error('Item id is not a string')
          refs.push(buildAnchorId(buildAnchorIdPrefix(item.id, 'content'), i))
        }
      }
    }
    matchRefs.current = refs
  }, [searchResults])

  useEffect(() => {
    handleMatchCount()
    handleMatchRefs()
  }, [searchResults])

  // Scroll to the current match anchor
  useEffect(() => {
    if (matchCount === 0) return
    setExpandedItems(true)
    const anchorId = matchRefs.current[currentMatch]
    if (!anchorId) return
    const el = document.getElementById(anchorId)
    if (!el) return
    el.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, [currentMatch, matchCount])

  const handlePrev = () => {
    setCurrentMatch((prev) => (prev - 1 + matchCount) % matchCount)
  }
  const handleNext = () => {
    setCurrentMatch((prev) => (prev + 1) % matchCount)
  }

  // Keyboard navigation for matches
  useEffect(() => {
    if (matchCount <= 0) return
    function handleKeyDown(e: KeyboardEvent) {
      const active = document.activeElement
      const isInput =
        active &&
        (active.tagName === 'INPUT' ||
          active.tagName === 'TEXTAREA' ||
          (active as HTMLElement).isContentEditable)
      if (isInput) return

      // Only respond to Meta+ArrowLeft/ArrowRight
      if (e.metaKey && e.key === 'ArrowLeft') {
        e.preventDefault()
        handlePrev()
      } else if (e.metaKey && e.key === 'ArrowRight') {
        e.preventDefault()
        handleNext()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [matchCount, handlePrev, handleNext])

  return (
    <>
      {!disabled && (
        <CardContent className="z-10 shadow-md">
          <div className="relative">
            <SearchIcon className="text-primary-400 absolute left-3 top-1/2 size-4 -translate-y-1/2 transform" />
            <Input
              ref={inputRef}
              type="search"
              value={query}
              className="pl-9"
              onChange={(e) => setQuery(e.target.value)}
              placeholder={tc('search', { cfl: true }) + '...'}
            />
          </div>
          {query.length > 1 && matchCount > 0 && (
            <div className="mt-4 flex items-center gap-2">
              <Button
                type="button"
                onClick={handlePrev}
                size="sm"
                variant="outline"
                disabled={matchCount <= 1}
              >
                {tc('previous', { cfl: true })}
                <div className="bg-primary-100 h-4 w-px" />
                <CommandShortcut>⌘←</CommandShortcut>
              </Button>
              <span className="text-primary-500 font-mono text-xs">
                Match {currentMatch + 1} of {matchCount}
              </span>
              <Button
                type="button"
                onClick={handleNext}
                size="sm"
                variant="outline"
                disabled={matchCount <= 1}
              >
                {tc('next', { cfl: true })}
                <div className="bg-primary-100 h-4 w-px" />
                <CommandShortcut>⌘→</CommandShortcut>
              </Button>
            </div>
          )}
        </CardContent>
      )}
      {children({
        currentMatch,
        expandedItems,
        matchCount,
        matchRefs,
        query,
        searchResults,
      })}
    </>
  )
}
